import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2 } from 'lucide-react';

// Talks directly to the primeslink-ai-assistant-vapi-n8n dashboard's public
// endpoints (a separate repo/deploy, owned by /ai-call-assistant-director) —
// primeslink.com never stores or validates leads itself, it's a thin
// cross-origin client. Requires that repo's /api/apply and
// /api/public-industries to allow this origin via CORS; until that's added
// there, submissions here will fail with a generic network error (caught
// below) even though the form itself is wired correctly.
const DASHBOARD_ORIGIN = 'https://portal.primeslink.com';
const LIVE_PHONE_DISPLAY = '(914) 529-0661';
const LIVE_PHONE_TEL = 'tel:+19145290661';

function IntakeField({ field, value, onChange }) {
  const label = (
    <label className="block text-sm font-medium text-gray-300 mb-1.5">
      {field.label}
      {field.required && <span className="text-accent-500"> *</span>}
    </label>
  );

  if (field.type === 'select') {
    return (
      <div>
        {label}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500"
        >
          <option value="" className="bg-dark-900">Select...</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt} className="bg-dark-900">
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      onChange(selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt]);
    };
    return (
      <div>
        {label}
        <div className="flex flex-wrap gap-2">
          {field.options.map((opt) => (
            <button
              type="button"
              key={opt}
              onClick={() => toggle(opt)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                selected.includes(opt)
                  ? 'bg-primary-500 border-primary-500 text-white'
                  : 'border-white/20 text-gray-300 hover:border-primary-500/50'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <div>
        {label}
        <div className="flex gap-2">
          {['Yes', 'No'].map((opt) => {
            const boolVal = opt === 'Yes';
            return (
              <button
                type="button"
                key={opt}
                onClick={() => onChange(boolVal)}
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  value === boolVal
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-white/20 text-gray-300 hover:border-primary-500/50'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {label}
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500 placeholder:text-gray-500"
      />
    </div>
  );
}

export default function AiCallAssistantApplyForm() {
  const [industries, setIndustries] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [industrySlug, setIndustrySlug] = useState('');
  const [intakeValues, setIntakeValues] = useState({});
  const [notes, setNotes] = useState('');

  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${DASHBOARD_ORIGIN}/api/public-industries`)
      .then((res) => {
        if (!res.ok) throw new Error(`request failed: ${res.status}`);
        return res.json();
      })
      .then((json) => setIndustries(json.industries))
      .catch(() => setLoadError('Could not load the form right now — please call us directly instead.'));
  }, []);

  // Lets a campaign link (e.g. ?industry=real_estate) land the visitor with
  // the right intake fields pre-selected, same convention as the dashboard's
  // own apply.html.
  useEffect(() => {
    if (!industries) return;
    const requested = new URLSearchParams(window.location.search).get('industry');
    if (!requested) return;
    const match = industries.find((i) => i.slug === requested);
    if (match) setIndustrySlug(match.slug);
  }, [industries]);

  const selectedIndustry = useMemo(
    () => industries?.find((i) => i.slug === industrySlug) ?? null,
    [industries, industrySlug]
  );

  function setIntakeValue(name, value) {
    setIntakeValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function handleIndustryChange(slug) {
    setIndustrySlug(slug);
    setIntakeValues({});
  }

  function runClientValidation() {
    const errors = {};
    if (!fullName.trim()) errors.full_name = 'Please enter your name.';
    if (!/^[\d\s()+-]{7,}$/.test(phone.trim())) errors.phone_number = 'Please enter a valid phone number.';
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) errors.email = 'Please enter a valid email.';
    if (!industrySlug) errors.industry_slug = 'Please select what we can help you with.';

    if (selectedIndustry) {
      for (const field of selectedIndustry.intake_fields) {
        if (!field.required) continue;
        const val = intakeValues[field.name];
        const empty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
        if (empty) errors[field.name] = `${field.label} is required.`;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    // Client-side check is for immediate feedback only — /api/apply always
    // re-validates everything server-side and is the only real authority.
    if (!runClientValidation()) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${DASHBOARD_ORIGIN}/api/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName,
          phone_number: phone,
          email,
          industry_slug: industrySlug,
          intake_data: intakeValues,
          notes: notes || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (Array.isArray(json.fieldErrors) && json.fieldErrors.length) {
          const errs = {};
          for (const fe of json.fieldErrors) errs[fe.field] = fe.message;
          setFieldErrors((prev) => ({ ...prev, ...errs }));
        }
        setSubmitError(json.error || 'Something went wrong — please try again or call us directly.');
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError('Something went wrong — please call us directly instead.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="glass rounded-2xl p-10 text-center max-w-xl mx-auto">
        <div className="w-14 h-14 rounded-full bg-primary-500/15 text-primary-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Thanks — we've got it!</h3>
        <p className="text-gray-400 text-sm">
          One of our AI voice assistants will call you back shortly at the number you provided.
        </p>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6 sm:p-8 max-w-xl mx-auto text-left">
      {loadError && <p className="text-sm text-red-400 mb-4">{loadError}</p>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Full Name <span className="text-accent-500">*</span>
            </label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500 placeholder:text-gray-500"
            />
            {fieldErrors.full_name && <p className="text-red-400 text-xs mt-1">{fieldErrors.full_name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Phone Number <span className="text-accent-500">*</span>
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500 placeholder:text-gray-500"
            />
            {fieldErrors.phone_number && <p className="text-red-400 text-xs mt-1">{fieldErrors.phone_number}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Email <span className="text-accent-500">*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
            className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500 placeholder:text-gray-500"
          />
          {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            What can we help you with? <span className="text-accent-500">*</span>
          </label>
          {industries ? (
            <select
              value={industrySlug}
              onChange={(e) => handleIndustryChange(e.target.value)}
              className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500"
            >
              <option value="" className="bg-dark-900">Select one...</option>
              {industries.map((i) => (
                <option key={i.slug} value={i.slug} className="bg-dark-900">
                  {i.display_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="h-12 w-full rounded-lg bg-white/5 animate-pulse" />
          )}
          {fieldErrors.industry_slug && <p className="text-red-400 text-xs mt-1">{fieldErrors.industry_slug}</p>}
        </div>

        {selectedIndustry && selectedIndustry.intake_fields.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-4 pt-4 border-t border-white/10"
          >
            {selectedIndustry.intake_fields.map((field) => (
              <div key={field.name}>
                <IntakeField field={field} value={intakeValues[field.name]} onChange={(v) => setIntakeValue(field.name, v)} />
                {fieldErrors[field.name] && <p className="text-red-400 text-xs mt-1">{fieldErrors[field.name]}</p>}
              </div>
            ))}
          </motion.div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Anything else we should know? (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg bg-white/5 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-primary-500 placeholder:text-gray-500"
          />
        </div>

        {submitError && <p className="text-sm text-red-400">{submitError}</p>}

        <motion.button
          type="submit"
          disabled={submitting}
          whileHover={{ scale: submitting ? 1 : 1.02 }}
          whileTap={{ scale: submitting ? 1 : 0.98 }}
          className="w-full px-6 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Submitting...
            </>
          ) : (
            'Request a Callback'
          )}
        </motion.button>

        <p className="text-center text-gray-500 text-sm">
          Prefer not to wait?{' '}
          <a href={LIVE_PHONE_TEL} className="text-primary-500 font-semibold hover:underline">
            Call {LIVE_PHONE_DISPLAY} now
          </a>
          .
        </p>
      </form>
    </div>
  );
}
